import type { CSSProperties } from "react";
import { ExtendedLink } from "../../components/ExtededLink/ExtendedLink";
import { SVGIcon } from "../../components/SVGIcon/SVGIcon";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { customSanitize } from "../../utils/customSanitize";
import { MediaFormatType } from "@/types/ModuleOptionsType";
import { ImageGridType } from "./ImageGrid.server";
import { downloadFile } from "../../utils/downloadFile";
import styles from "./ImageGridCard.module.scss";
import { ContentTag } from "@/components/ContentTag/ContentTag";
import { Button, ButtonTypes } from "@/components/Button/Button";
import { MediaItem } from "@/components/MediaItem/MediaItem";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG.client";
import CardLinkOverlay from "@/components/CardLinkOverlay/CardLinkOverlay.tsx";

interface ImageGridCardProps {
  element: Exclude<ImageGridType["image_grid"]["elements"], false>[number];
  gridType: ImageGridType["image_grid"]["grid_type"];
  showBoxShadow: boolean | undefined;
  showBorderRadius: boolean | undefined;
  showBox: boolean | undefined;
  mediaFormat: MediaFormatType;
  textAlignClass: string;
  showExtendedLink: boolean;
  type: "image" | "download";
  buttonType: ButtonTypes;
  textColor?: string;
  overlayStyle?: CSSProperties;
  heading_level?: number;
}

const ImageGridCard = ({
  showBoxShadow,
  showBorderRadius,
  element,
  mediaFormat,
  gridType,
  showExtendedLink,
  textAlignClass,
  type,
  buttonType,
  showBox,
  textColor,
  overlayStyle,
  heading_level = 3,
}: ImageGridCardProps) => {
  const { getLabel, getIcon } = useGlobalContext();

  const titleInImage = gridType === "title-in-image" && type === "image";

  const btnJSX = element.link && (
    <Button
      type={buttonType}
      href={element.link.url}
      target={element.link.target}
      aria-label={
        element.link.aria_label ??
        `${getLabel("read_more_label")}: ${element.title}`
      }
      className={`${styles["link-btn"]} ${titleInImage ? "mt-2" : ""}`}
      disablePrefetch // Disable prefetching for grid links https://trello.com/c/g5k075FS
    >
      {getLabel("read_more_label")}
    </Button>
  );
  return (
    <article
      data-shadow={showBox && showBoxShadow}
      data-border-radius={showBox && showBorderRadius}
      className={`${styles["image-grid-element"]}`}
    >
      <CardLinkOverlay
        title={element.title}
        link={element.link || undefined}
        getLabel={getLabel}
      />
      <div className={`${styles["image-wrapper"]}  `}>
        {titleInImage && (
          <>
            {/* <div className={styles.overlay}></div> */}
            <header className={`${styles["btn-wrapper"]}`}>
              <p
                role="heading"
                aria-level={heading_level}
                className={`title pb-0`}
                style={
                  textColor
                    ? { color: ` var(--${textColor}) !important ` }
                    : undefined
                }
                dangerouslySetInnerHTML={{
                  __html: customSanitize(element.title),
                }}
              />
              {btnJSX}
              {showExtendedLink && element.link_extended && (
                <ExtendedLink
                  extendedLink={element.link_extended}
                  btnType={buttonType}
                  extraClass="mt-2"
                  getLabel={getLabel}
                />
              )}
            </header>
          </>
        )}

        {element.image && (
          <>
            {element.image.mime_type === "image/svg+xml" ? (
              <SVGIcon src={element.image.url} />
            ) : (
              <MediaItem
                hasOverlay={titleInImage}
                overlayStyle={overlayStyle}
                image_desktop={element.image}
                mediaFormat={mediaFormat}
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                copyrightPosition={
                  gridType === "title-in-image" ? "top" : "bottom"
                }
                infoButtonPosition={
                  gridType === "title-in-image" ? "top" : "bottom"
                }
              />
            )}
          </>
        )}

        {type === "download" && element?.download?.link_target && (
          <div className={styles["download-wrapper"]}>
            {btnJSX}
            <button
              aria-label={element.download.link_aria_label}
              type="button"
              className={`${styles["download-link-icon"]} ${"m-0"}`}
              onClick={() => {
                if (element?.download?.link_target)
                  downloadFile(element.download.link_target.url).catch((e) => {
                    console.error("Download failed:", e);
                  });
              }}
            >
              <CustomSVG
                src={getIcon("download")}
                wrapper={"span"}
                width={38}
                height={38}
                aria-hidden="true"
                className="is-flex"
              />
            </button>
          </div>
        )}
      </div>
      {gridType === "text-below-image" && (
        <div
          className={`${styles["content-wrapper"]} ${textAlignClass} ${showBox ? "is-box" : ""}`}
        >
          <header>
            {" "}
            {element.title && (
              <p
                role="heading"
                aria-level={heading_level}
                className={`title`}
                style={textColor ? { color: textColor } : undefined}
                dangerouslySetInnerHTML={{
                  __html: customSanitize(element.title),
                }}
              />
            )}
            {element.content && <ContentTag content={element.content} />}
          </header>

          <footer data-align={textAlignClass} className={styles["btn-wrapper"]}>
            {btnJSX}
            {showExtendedLink && element.link_extended && (
              <ExtendedLink
                extendedLink={element.link_extended}
                btnType={buttonType}
                getLabel={getLabel}
                extraClass="mt-0"
              />
            )}
          </footer>
        </div>
      )}
    </article>
  );
};

export default ImageGridCard;
